/**
 * Shared helpers and UI for displaying post/tweet media in X Investigation (User & Event mode).
 */

import { ExternalLink } from 'lucide-react';

/** Extract all displayable media URLs from a tweet/post object (any API shape). */
export function getMediaUrlsFromPost(post: Record<string, any> | null | undefined): string[] {
  if (!post || typeof post !== 'object') return [];
  const urls: string[] = [];

  // Direct arrays
  const mediaUrls = post.media_urls;
  if (Array.isArray(mediaUrls)) {
    mediaUrls.forEach((u: unknown) => typeof u === 'string' && u.startsWith('http') && urls.push(u));
  }

  // media: array of objects or strings, or single object
  let media = post.media;
  if (media != null && !Array.isArray(media) && typeof media === 'object') media = [media];
  if (Array.isArray(media)) {
    media.forEach((m: any) => {
      if (typeof m === 'string' && m.startsWith('http')) {
        urls.push(m);
        return;
      }
      if (m && typeof m === 'object') {
        const u = m.mediaUrlHttps ?? m.media_url_https ?? m.media_url ?? m.mediaUrl ?? m.src;
        if (typeof u === 'string' && u.startsWith('http')) urls.push(u);
        // Video variants (twitterapi.io uses videoVariants, standard uses variants)
        const variants = m.videoVariants ?? m.variants;
        if (Array.isArray(variants)) {
          const sorted = [...variants]
            .filter((v: any) => typeof v?.url === 'string' && v.url.startsWith('http'))
            .sort((a: any, b: any) => (b.bitrate ?? 0) - (a.bitrate ?? 0));
          if (sorted.length > 0) urls.push(sorted[0].url);
        }
      }
    });
  }

  // photos: array of { url } or string
  const photos = post.photos;
  if (Array.isArray(photos)) {
    photos.forEach((p: any) => {
      const u = typeof p === 'string' ? p : p?.url;
      if (typeof u === 'string' && u.startsWith('http')) urls.push(u);
    });
  }

  // attachments.media (Twitter API / adapter shape)
  const attachments = post.attachments;
  if (attachments && typeof attachments === 'object' && Array.isArray(attachments.media)) {
    attachments.media.forEach((m: any) => {
      if (!m || typeof m !== 'object') return;
      const u = m.url ?? m.media_url_https ?? m.media_url;
      if (typeof u === 'string' && u.startsWith('http')) urls.push(u);
      const v = m.video;
      if (v && typeof v === 'object' && typeof v.url === 'string' && v.url.startsWith('http')) urls.push(v.url);
      (m.variants || []).forEach((v: any) => {
        const vurl = v?.url;
        if (typeof vurl === 'string' && vurl.startsWith('http')) urls.push(vurl);
      });
    });
  }

  // video: { url } or { variants: [{ url }] }
  const video = post.video;
  if (video && typeof video === 'object') {
    const u = video.url;
    if (typeof u === 'string' && u.startsWith('http')) urls.push(u);
    const variants = video.variants;
    if (Array.isArray(variants)) {
      variants.forEach((v: any) => {
        const vurl = v?.url;
        if (typeof vurl === 'string' && vurl.startsWith('http')) urls.push(vurl);
      });
    }
  }

  // entities.media / extended_entities.media — both snake_case and camelCase (twitterapi.io raw shape)
  for (const key of ['entities', 'extended_entities', 'extendedEntities', 'legacy', 'legacyExtended']) {
    const ent = post[key];
    if (!ent || typeof ent !== 'object') continue;
    const mediaList = ent.media;
    if (!Array.isArray(mediaList)) continue;
    mediaList.forEach((m: any) => {
      if (!m || typeof m !== 'object') return;
      // camelCase (twitterapi.io) and snake_case (standard Twitter API) variants
      const u = m.mediaUrlHttps ?? m.media_url_https ?? m.mediaUrl ?? m.media_url ?? m.url;
      if (typeof u === 'string' && u.startsWith('http')) urls.push(u);
      // Video variants (camelCase: videoVariants, snake_case: video_info.variants)
      const videoVariants = m.videoVariants ?? m.video_info?.variants ?? m.variants;
      if (Array.isArray(videoVariants)) {
        const best = [...videoVariants]
          .filter((v: any) => typeof v?.url === 'string' && v.url.startsWith('http'))
          .sort((a: any, b: any) => (b.bitrate ?? 0) - (a.bitrate ?? 0));
        if (best.length > 0) urls.push(best[0].url);
      }
    });
  }

  // Dedupe and return
  return [...new Set(urls)];
}

function isVideoUrl(url: string): boolean {
  return !!(
    url.includes('video') ||
    url.includes('.mp4') ||
    url.includes('amplify_video') ||
    url.includes('video.twimg.com') ||
    url.includes('.m3u8')
  );
}

interface PostMediaGridProps {
  mediaUrls: string[];
  maxItems?: number;
  className?: string;
}

/** Renders a grid of images/videos for a post. */
export function PostMediaGrid({ mediaUrls, maxItems = 6, className = '' }: PostMediaGridProps) {
  const list = (mediaUrls || []).filter((u) => typeof u === 'string' && u.startsWith('http')).slice(0, maxItems);
  if (list.length === 0) return null;

  const gridClass =
    list.length === 1 ? 'grid-cols-1' : list.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3';

  return (
    <div className={`grid gap-2 mt-2 ${gridClass} ${className}`}>
      {list.map((mediaUrl: string, mediaIndex: number) => {
        const video = isVideoUrl(mediaUrl);
        return video ? (
          <div key={mediaIndex} className="relative rounded-lg overflow-hidden bg-black">
            <video
              src={mediaUrl}
              controls
              className="w-full h-auto max-h-96 object-contain"
              onError={(e) => {
                const el = e.target as HTMLVideoElement;
                el.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div
            key={mediaIndex}
            className="relative rounded-lg overflow-hidden bg-gray-800 group cursor-pointer"
          >
            <img
              src={mediaUrl}
              alt={`Media ${mediaIndex + 1}`}
              className="w-full h-auto max-h-96 object-cover hover:opacity-90 transition-opacity"
              onClick={() => window.open(mediaUrl, '_blank')}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <ExternalLink className="w-6 h-6 text-white" />
            </div>
          </div>
        );
      })}
    </div>
  );
}
